import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Typography, CircularProgress, Alert, Snackbar, Paper,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

const MAX_WHAT_YOU_SELL_LEN = 10000;
const MAX_STRUCTURED_FIELD_LEN = 1000;

interface OrganizationSettings {
  org_id: string;
  name: string;
  whatYouSell: string;
  targetAudience: string;
  howItWorks: string;
  certifications: string[];
  differentiators: string[];
}

/** Comma-or-newline-separated text <-> string[], trimming blanks either way —
 *  same shape the onboarding wizard's certifications/differentiators answers
 *  already produce, so a value round-trips whichever side wrote it last. */
const listToText = (items: string[]): string => (items || []).join(', ');
const textToList = (text: string): string[] =>
  text.split(/[,\n]/).map((item) => item.trim()).filter(Boolean);

const emptyForm = {
  whatYouSell: '',
  targetAudience: '',
  howItWorks: '',
  certifications: '',
  differentiators: '',
};

/** The structured company profile is the single editable authority. The
 * backend derives the legacy text representation for runtime consumers. */
const CompanyContextCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [original, setOriginal] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await apiService.get<OrganizationSettings>('/settings/organization');
        if (cancelled) return;
        const loaded = {
          whatYouSell: resp?.data?.whatYouSell ?? '',
          targetAudience: resp?.data?.targetAudience ?? '',
          howItWorks: resp?.data?.howItWorks ?? '',
          certifications: listToText(resp?.data?.certifications ?? []),
          differentiators: listToText(resp?.data?.differentiators ?? []),
        };
        setForm(loaded);
        setOriginal(loaded);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load company context');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setField = (field: keyof typeof emptyForm) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const dirty = JSON.stringify(form) !== JSON.stringify(original);
  const overLimit =
    form.whatYouSell.length > MAX_WHAT_YOU_SELL_LEN ||
    form.targetAudience.length > MAX_STRUCTURED_FIELD_LEN ||
    form.howItWorks.length > MAX_STRUCTURED_FIELD_LEN ||
    form.certifications.length > MAX_STRUCTURED_FIELD_LEN ||
    form.differentiators.length > MAX_STRUCTURED_FIELD_LEN;

  const handleSave = async () => {
    if (!dirty || overLimit) return;
    try {
      setSaving(true);
      setError(null);
      const resp = await apiService.put<OrganizationSettings>('/settings/organization', {
        whatYouSell: form.whatYouSell,
        targetAudience: form.targetAudience,
        howItWorks: form.howItWorks,
        certifications: textToList(form.certifications),
        differentiators: textToList(form.differentiators),
      });
      const saved = {
        whatYouSell: resp?.data?.whatYouSell ?? form.whatYouSell,
        targetAudience: resp?.data?.targetAudience ?? form.targetAudience,
        howItWorks: resp?.data?.howItWorks ?? form.howItWorks,
        certifications: listToText(resp?.data?.certifications ?? textToList(form.certifications)),
        differentiators: listToText(resp?.data?.differentiators ?? textToList(form.differentiators)),
      };
      setForm(saved);
      setOriginal(saved);
      setSavedToast(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to save company context');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={0} className="kb-company-card">
      <Box className="kb-company-intro">
        <Typography className="kb-company-kicker">Shared business context</Typography>
        <Typography variant="body2" color="text.secondary">
          Keep these details current so guidance, training, and generated drafts
          use the same business context. Saving updates the runtime summary
          automatically; it does not publish Knowledge Base content.
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {/* The legacy free-text note was removed on 2026-09-22. These fields
              are the only company context there is now, so an empty profile
              means every prompt runs with no grounding at all. Say that
              plainly rather than letting it look optional. */}
          {!form.whatYouSell && !form.targetAudience && !form.howItWorks && !form.certifications && !form.differentiators && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nothing is filled in yet. Training and playbook generation use these details to
              know what you sell. Until they are set, the AI customer has no idea what it is
              being pitched, and playbook tracks cannot be created at all.
            </Alert>
          )}
          <Box className="kb-company-grid">
            <TextField
              className="kb-company-field kb-company-field-wide"
              fullWidth
              multiline
              minRows={3}
              label="What you sell"
              value={form.whatYouSell}
              onChange={setField('whatYouSell')}
              placeholder="A rough sentence is enough."
              variant="outlined"
              error={form.whatYouSell.length > MAX_WHAT_YOU_SELL_LEN}
              helperText={`${Math.max(0, MAX_WHAT_YOU_SELL_LEN - form.whatYouSell.length)} characters remaining`}
              inputProps={{ style: { fontSize: 14 } }}
            />
            <TextField
              className="kb-company-field"
              fullWidth
              multiline
              minRows={2}
              label="Target audience"
              value={form.targetAudience}
              onChange={setField('targetAudience')}
              placeholder="Who are the main buyers or customer types your reps usually speak with?"
              variant="outlined"
              error={form.targetAudience.length > MAX_STRUCTURED_FIELD_LEN}
              helperText={`${Math.max(0, MAX_STRUCTURED_FIELD_LEN - form.targetAudience.length)} characters remaining`}
              inputProps={{ style: { fontSize: 14 } }}
            />
            <TextField
              className="kb-company-field"
              fullWidth
              multiline
              minRows={2}
              label="How it works"
              value={form.howItWorks}
              onChange={setField('howItWorks')}
              placeholder="How does it work, from the customer's point of view?"
              variant="outlined"
              error={form.howItWorks.length > MAX_STRUCTURED_FIELD_LEN}
              helperText={`${Math.max(0, MAX_STRUCTURED_FIELD_LEN - form.howItWorks.length)} characters remaining`}
              inputProps={{ style: { fontSize: 14 } }}
            />
            <TextField
              className="kb-company-field"
              fullWidth
              label="Certifications"
              value={form.certifications}
              onChange={setField('certifications')}
              placeholder="Comma-separated, e.g. ISO 9001, FDA registered"
              variant="outlined"
              error={form.certifications.length > MAX_STRUCTURED_FIELD_LEN}
              helperText={`${Math.max(0, MAX_STRUCTURED_FIELD_LEN - form.certifications.length)} characters remaining`}
              inputProps={{ style: { fontSize: 14 } }}
            />
            <TextField
              className="kb-company-field"
              fullWidth
              label="Differentiators"
              value={form.differentiators}
              onChange={setField('differentiators')}
              placeholder="Comma-separated, e.g. 3x faster cycle, fully biodegradable"
              variant="outlined"
              error={form.differentiators.length > MAX_STRUCTURED_FIELD_LEN}
              helperText={`${Math.max(0, MAX_STRUCTURED_FIELD_LEN - form.differentiators.length)} characters remaining`}
              inputProps={{ style: { fontSize: 14 } }}
            />
          </Box>

          <Box className="kb-company-actions">
            <Button
              className="kb-company-save"
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={!dirty || overLimit || saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={savedToast}
        autoHideDuration={3000}
        onClose={() => setSavedToast(false)}
        message="Company details saved"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default CompanyContextCard;
